import type { ScoreBand } from "@/api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { SCORE_BAND_TONE } from "@/lib/risk-helpers";

interface ScoreBandBadgeProps {
  band: ScoreBand | string | null | undefined;
}

// Each score band maps to a translation key so the badge label
// re-renders on a locale switch.
const SCORE_BAND_KEY: Record<ScoreBand, TranslationKey> = {
  critical: "risk.bands.critical",
  needs_attention: "risk.bands.needsAttention",
  good: "risk.bands.good",
  excellent: "risk.bands.excellent",
};

/** Renders a localized label + Ocean Blue palette tone for a risk
 *  score band. Accepts ``null`` so the dashboard can render a
 *  neutral placeholder before the snapshot loads. */
export function ScoreBandBadge({ band }: ScoreBandBadgeProps) {
  const { t } = useTranslation();
  if (!band) {
    return <Badge tone="neutral">—</Badge>;
  }
  const known = band as ScoreBand;
  const key = SCORE_BAND_KEY[known];
  // Fall back to the raw backend value when the band is not one of
  // the known enum members.
  const label = key ? t(key) : band;
  const tone = SCORE_BAND_TONE[known] ?? "neutral";
  return <Badge tone={tone}>{label}</Badge>;
}
